import { BillingType } from '~constants/maps';

import { Lookup, SahCategory, SahClaimStatus, SahServiceInfo, StatementStatus, Supplement } from '~lookups/models';

import { SahRegisteredServiceType } from '~support-at-home/models/sahProviderSummary';

import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';
import { ClientRow } from '~main/reducers/clients/clients';

export interface ClientService {
  id: string;
  company_id?: number;
  client_id?: number;
  service_id?: string;
  start_date?: string;
  end_date?: string;
  meta_information?: any;
}

export interface ClientContribution {
  id: string;
  company_id?: number;
  client_id?: number;
  category_id?: string;
  contrib_proportion?: number;
  start_date?: string;
  end_date?: string;
  meta_information?: any;
}

export interface SahBudget {
  id: string;
  company_id?: number;
  client_id?: number;
  sah_budget_id?: string;
  funding_source_id?: number;
  start_date?: string;
  end_date?: string;
  total_amount?: number;
  available_amount?: number;
  used_amount?: number;
  origin_tag?: string;
  is_archived?: boolean;
  meta_information?: any;
  prior_spent_amount?: number | null;
  prior_spent_date?: string | null;
  prior_spent_included_in_statements?: boolean;
  status?: string;
  budget_type_code?: string;
  care_recipient_id?: string;
  write_off?: number;
  rollover_deduction?: number;
  last_api_sync_at?: string;
  service_naps_id?: string;
  // TAV-2601 / TAV-2602 — set only by the API's rollover / elect-to-return action lib, which
  // bypasses the ordinary update path so these can never be reverted by an editable-table save.
  rollover_requested_at?: string | null;
  is_elected_to_return?: boolean;
  elected_to_return_at?: string | null;
  // TAV-3332. Only populated by `getAll`'s list-row include (Budgets.tsx); `getOne` (BudgetsModal)
  // never sets it. Used to name the client in the rollover / elect-to-return dialogs without an
  // extra cross-DB query on every modal open.
  client?: Pick<ClientRow, 'id' | 'full_name'> | null;
}

// Services Australia's Budgets 1.3.0 Message shape, returned by both the rollover and
// elect-to-return trigger endpoints. Every field is optional per the vendor schema.
export interface SahMessage {
  type?: 'I' | 'W' | 'E';
  shortText?: string;
  longText?: string;
}

interface SahClients {
  id: string | number;
  first_name: string;
  surname: string;
}

export interface SahClientServicesGrouped extends SahClients {
  client_id: number;
  services_count: number;
}

export interface SahClientContributionsGrouped extends SahClients {
  contributions_count: number;
}

export interface SupplierInvoiceItem {
  id: string;
  invoice_id?: string;
  client_budget_id?: string;
  client_package_id?: number;
  service_type_id?: number;
  service_date?: string;
  purchase_order_number?: string;
  description?: string;
  notes?: string;
  units?: number;
  unit_amount?: number;
  gst_type?: 'free' | 'in' | 'nin';
  account_code_id?: string;
  sah_item_id?: string;
  sah_wrap_id?: string;
  sah_wrap_unit_type?: string | null;
  is_first_payment_item?: boolean | null;
  [key: string]: any;
}

export interface ClientHcpUnspentFunds {
  funding_type_name: string | null;
  total_amount: number | null;
  available_amount: number | null;
  used_amount: number | null;
  updated_at: string;
}

// budgets
export interface Budget {
  id: string;
  reference: string;
  client_id: number;
  client_sah_budget_id: string;
  care_plan_id?: string | null;
  client_package_id: number;
  price_plan_id: string;
  price_plan_revision_id: string;
  package_fixed_id: number;
  status_id: string;
  status?: ClientBudgetStatus;
  start_date: string;
  end_date: string;
  total_amount: number;
  available_amount?: number;
  total_allocated?: number;
  total_includes_case_management?: boolean;
  sections: Section[];
  price_plan_revision: PricePlanRevision;
  sah_budget: SahBudget;
  created_at: string;
  client: ClientRow;
  grand_total: SectionTotal;
  package_fixed: Partial<ClientPackage>;
  supplements?: Supplement[];
  ignore_sah_filters?: boolean;
  is_over_allocated?: boolean;
  needs_review?: boolean;
  removed_count?: number;
  price_missing_count?: number;
  supplement_rates_stale?: boolean;
}

export interface ClientBudgetStatus {
  id: string;
  name: string;
  tag?: string;
}

export interface Section {
  id: string;
  category_id: string;
  category_name: string;
  contrib_proportion: number | null;
  items: SectionItem[];
  totals: SectionTotal;
  service_totals?: ServiceTotal[];
  category?: SahCategory;
}

export const COPY_STATUS = {
  CARRIED_OVER: 'carried_over',
  PRICE_CHANGED: 'price_changed',
  REMOVED: 'removed',
  /**
   * The service's contribution category changes over the dates being copied into, so the line is
   * carried into the category that applies rather than dropped. Distinct from REMOVED: the line is
   * still in the budget, still editable and still saved - it has just changed section.
   */
  CATEGORY_MOVED: 'category_moved',
  NEW: 'new',
} as const;

export type CopyStatus = (typeof COPY_STATUS)[keyof typeof COPY_STATUS];

export const REVIEW_STATE = {
  ON_PLAN: 'on_plan',
  FILTERED: 'filtered',
  NOT_ON_PLAN: 'not_on_plan',
  PRICE_MISSING: 'price_missing',
} as const;

export type ReviewState = (typeof REVIEW_STATE)[keyof typeof REVIEW_STATE];

export interface SectionItem {
  id: string;
  frequency_id: string;
  section_id?: string;
  shift_id: string;
  service_type_id: string;
  start_date: string;
  end_date: string;
  week_days?: number[];
  quantity?: number;
  unit_type?: string;
  unit_amount?: number;
  unit_amount_ph?: number;
  total_amount?: number;
  include_public_holidays: boolean;
  is_external_provider: boolean;
  external_reference?: string;
  generate_provider_orders?: boolean;
  category_id?: string;
  service_type?: Partial<ServiceTypeRow>;
  week_days_text?: string;
  shift?: {
    id: string;
    name: string;
  };
  frequency?: {
    id: string;
    name: string;
  };
  billing_type?: BillingType;
  copy_status?: CopyStatus;
  /**
   * Why a copy dropped this line, where the reason changes how it reads to a provider. Only
   * `unavailable_for_dates` is sent: a service that IS on the target price plan but cannot be dated
   * to the new range under its category needs a different explanation from one that is not on the
   * plan at all, and the two were previously given the same, wrong one.
   */
  removed_reason?: string;
  /**
   * Both ends of a contribution category change, where the copy found one for this line.
   *
   * Sent whether or not the move is being applied - a provider who has declined it still has to be
   * told what they declined, and still has to be able to change their mind.
   */
  moved_from_category_id?: string;
  moved_from_category?: SahCategory;
  moved_to_category_id?: string;
  moved_to_category?: SahCategory;
  /** The date the destination category takes effect for this service - the day the change lands. */
  moved_on?: string | null;
  previous_unit_amount?: number;
  review_state?: ReviewState;
}

export interface ServiceTotal {
  service_type_id: string;
  total: number;
  contribution: number;
  govSubsidy: number;
}

export interface ExternalProviderData {
  is_external_provider: boolean;
  external_reference?: string;
}

export interface SectionTotal {
  cost: number;
  contribution: number;
  govSubsidy: number;
  case_management: number;
}

export interface BudgetActivitiesLog {
  external_reference_user_name: string;
  feature_tag: string;
  type_tag: string;
  created_at: string;
  changes: {
    budget: Detail[];
    sections: SectionChange[];
    items: ItemChange[];
  };
}

export interface SectionChange {
  section_id: string;
  category: SahCategory;
  details: Detail[];
}

export interface ItemChange {
  section_id: string;
  item_id?: string;
  service_type_id: number;
  details: Detail[];
}

export interface Detail {
  field: string;
  from?: string;
  to: string;
}

// price-plans
export interface PricePlan {
  id: string;
  company_id: number;
  name: string;
  start_date: string;
  end_date: string;
  is_deleted: boolean;
  revisions?: PricePlanRevision[];
}

export type PricePlanRow = Omit<PricePlan, 'revisions'> & {
  latest_revision: PricePlanRevision;
};

export type PricePlanModalRow = {
  id: string;
  category_id?: string;
  service_type_id: number;
  billing_type?: BillingType;
  [shiftId: string]: number | string | Record<string, PricePlanService> | ServiceTypeRow | undefined;
};

// price-plan-revisions
export interface PricePlanRevision {
  id: string;
  plan_id: string;
  sequence: number;
  services: PricePlanService[];
  billing_rules: PricePlanBillingRule[];
  price_plan?: PricePlan;
  is_latest_revision?: boolean;
}

export interface PricePlanService {
  id: string;
  revision_id: string;
  service_type_id: number;
  category_id: string;
  shift_id: string;
  cost: number;
  service_type: ServiceTypeRow;
  service_type_assoc?: {
    sah_service: {
      category: {
        id: string;
        name: string;
        tag: string;
      };
    };
  };
}

export interface PricePlanBillingRule {
  id: string;
  billing_type: BillingType;
  service_type_id: number;
  category_id: string;
  service_type_assoc?: {
    sah_service: {
      category: {
        id: string;
        name: string;
        tag: string;
      };
    };
  };
}

export interface BudgetType {
  id: string;
  sah_reference_id: string;
  name: string;
  tag: string;
}

export interface SupplierInvoice {
  id: string;
  client_id: number;
  supplier_id: string;
  created_at: string;
  reference: string;
  due_date: string;
  invoice_date: string;
  status_id: string;
  status?: SupplierInvoiceStatus;
  items?: SupplierInvoiceItem[];
  file?: InvoiceFile;
  files?: InvoiceFile[];
  net_total?: number;
  gross_total?: number;
}

export interface InvoiceFile {
  id: string;
  invoice_id: string;
  file_name: string;
  file_type: string; // e.g., "image/jpeg", "application/pdf"
  file_path: string;
  data?: string | null; // Object URL (e.g., "blob:http://localhost:3000/abc123...")
}

export interface AnalyseInvoiceResponse {
  invoice: {
    reference: string | null;
    invoice_date: string | null;
    due_date: string | null;
    supplier_name: string | null;
    client_name: string | null;
  };
  items: Array<{
    description: string | null;
    service_date: string | null;
    units: number | null;
    unit_amount: number | null;
    gst_type: 'free' | 'in' | 'nin';
  }>;
  confidence: Record<string, number>;
}

export interface Supplier {
  id: string;
  company_id: number;
  name: string;
  account_reference: string;
}

export interface SupplierInvoiceActivityItemChange {
  item_id: string;
  changes: Detail[];
}

export interface SupplierInvoiceActivityLog {
  external_reference_user_name: string | null;
  feature_tag: string;
  type_tag: string;
  created_at: string;
  changes: {
    invoice: Detail[];
    items: SupplierInvoiceActivityItemChange[];
  };
}

export interface SupplierInvoiceStatus {
  id: string;
  name: string;
  tag: string;
  to_statuses: [{ from_status_id: string }];
  from_statuses: [{ to_status_id: string }];
  createdby_id: number;
  created_at: string;
}

export interface ClientWithBudget {
  id: number;
  first_name: string;
  surname: string;
  full_name: string;
  budgets: Array<Budget>;
  latest_budget?: Budget | null;
}

export interface ClientPackage {
  id: number;
  name: string;
  company_id: number;
  funding_type_id: number;
  funding_type: FundingType;
}

export interface FundingType {
  id: number;
  name: string;
  sequence: number;
  company_id: number;
  is_deleted?: boolean;
  sah_code?: string;
}

// Models for Budget Tracker
export interface Tracker {
  client: { id: number; full_name: string };
  start: string;
  end: string;
  status: 'draft' | 'active' | 'expired' | 'closed';
  total_amount: number;
  summary: TrackerData;
  categories: TrackerCategory[];
  services: TrackerService[];
}

export interface TrackerData {
  allocated: number;
  booked: number;
  claimed: number;
  remaining: number;
  overspend: number;
  utilization: number;
  units: 'currency' | 'hours';
}

export interface TrackerCategory {
  id: string;
  name: string;
  code: string;
  data: TrackerData;
}

export interface TrackerService {
  id: string;
  category_id: string;
  name: string;
  code: string;
  data: TrackerData;
}

// Claims
export interface Claim {
  id: string;
  reference: string;
  status: SahClaimStatus;
  company_id: number;
  sah_reference_id: string;
  start_date: string;
  end_date: string;
  is_validating: boolean;
  validated_at?: string;
  items?: ClaimItem[];
  items_count?: number;
  items_valid?: number;
  items_with_errors?: number;
  items_ignored?: number;
  // Services Australia invoice push state (TAV-2590), persisted on the claim so the UI never
  // depends on the ephemeral finalise response alone. `sa_invoice_id` is the gate the whole
  // feature keys off — see the comment above <ClaimApiPushSummary> in Claim.tsx for why.
  sa_invoice_id?: string | null;
  sa_invoice_status?: string | null;
  // Raw Services Australia invoice summary, minus its items[] array. `messages` is the persisted
  // copy of the invoice-level diagnostics behind AC #7 — the API writes it here after a push so the
  // detail survives a page reload, not just the finalise response.
  sa_meta_information?: ({ messages?: ClaimApiPushMessage[] } & Record<string, unknown>) | null;
  // Only present once `sa_invoice_id` is set (the API only computes these for API claims).
  items_pushed?: number;
  items_pending_push?: number;
  items_attachment_required?: number;
  items_further_info_required?: number;
  // TAV-2592. Two counts, not one, with a different consumer each.
  //
  // `items_late_submission` is REACHABLE (flagged, reason captured or not). It is one of the three
  // conditions gating the `late_submission` column - the requirements trigger's home for a claim
  // that is finalised but holds no SA invoice - and is reachability rather than outstanding-ness
  // precisely so the column survives a reason being captured, instead of vanishing under the user
  // who just used it (TAV-2591's paperclip lesson). The API computes it unconditionally, outside the
  // `sa_invoice_id` guard the four SA counts sit behind, so it is readable before any push.
  //
  // `items_late_submission_required` is OUTSTANDING (flagged, no reason yet) and drives the push
  // summary banner's line and its drill-down filter, which are post-push by nature.
  //
  // Both `0` for flat-file providers - the API never writes either flag for them.
  items_late_submission?: number;
  items_late_submission_required?: number;
  // TAV-2594. Set once this claim has been swept into a Services Australia CLAIM (the wrapper
  // around N submitted invoices) - a different vendor object from the invoice above. `sa_claim_id`
  // gates the claim-status line in ClaimApiPushSummary; `sa_claim_status` is the raw Claim 1.0.0
  // enum (`Being Calculated` etc.) - a DIFFERENT axis from `sa_invoice_status`, see
  // SA_CLAIM_STATUS_COPY.
  sa_claim_id?: string | null;
  sa_claim_status?: string | null;
  // TAV-2595. Present once the claim's Services Australia claim has been paid (Claim 1.0.0
  // `paymentDate`) - null/absent otherwise. Flattened from the new `company_sa_claims` parent
  // table alongside sa_claim_id/sa_claim_status above; the API contract is otherwise unchanged.
  payment_date?: string | null;
  // TAV-2595 (AC #4). Services Australia's CLAIM-level diagnostics, flattened from
  // `company_sa_claims.meta_information.messages`. Deliberately a DIFFERENT field from
  // `sa_meta_information.messages` above, which is the Invoices 1.2.0 INVOICE-level array - two
  // unrelated vendor axes that happen to share the word "messages". Populated only for claims whose
  // SA status changed on a sweep (the detail call is the only place messages[] exists).
  sa_claim_messages?: ClaimApiPushMessage[] | null;
  // TAV-3234 (AC2). When Services Australia's state for THIS claim was last read back, flattened from
  // company_sa_claims.last_synced_at. Distinct from ClaimableResponse.claims_last_synced_at on the
  // claims LIST, which is a company-wide MAX across every claim. Null until the claim has been lodged:
  // the company_sa_claims parent row that carries this column is created by /claims/submit-claim.
  sa_last_synced_at?: string | null;
  // Whether PRODA direct claiming is available for this company in this environment, returned by
  // `GET /claims/:id`. As of TAV-3192 this is the `feature-proda-direct` flag AND the company's
  // Services Australia connection, not the connection alone. Distinct from every other field here:
  // it describes the provider, not the claim.
  //
  // It exists because the primary action has to be chosen BEFORE any invoice exists - "Send to
  // Services Australia" when available, plain "Finalise" otherwise - so `sa_invoice_id` (the gate
  // ClaimApiPushSummary uses) cannot answer it. The flag is resolved company-wide with no user
  // identity in the evaluation context, so this is a shared action every user sees identically;
  // there is no per-user divergence to guard against.
  sa_connected?: boolean;
}

export interface ClaimItem {
  id: string;
  claim: Claim;
  client_id: number;
  client: { id: number; full_name: string };
  service_date: string;
  sah_service_id?: string;
  sah_wrap_id?: string;
  sah_item_id?: string;
  sah_detail_description?: string;
  sah_hpt_id?: string;
  sah_hpt_description?: string;
  is_first_payment_item?: boolean | null;
  is_prescribed_item?: boolean | null;
  purchase_method?: string | null;
  sah_wrap_unit_type?: string | null;
  client_budget_id?: string;
  origin_tag?: string;
  origin_appointment_id?: number;
  origin_supplier_invoice_item_id?: string;
  units?: number;
  unit_amount?: number;
  is_ignored?: boolean;
  is_evidence_required?: boolean;
  // Per-item Services Australia push state (TAV-2590). `sa_invoice_item_id` is the SA-assigned
  // invoice item id (distinct from `sah_item_id` above, which is the FK to the service-item
  // lookup) — its presence means this item has been successfully pushed. `null`/`undefined` on
  // the boolean flags means "not pushed yet / unknown", not "false" — see ClaimSahPushIcon.
  sa_invoice_item_id?: string | null;
  sa_attachment_required?: boolean | null;
  // SA's `Item.attachmentProvided`. Read BOTH flags - neither is sufficient alone. "Still needs
  // evidence" is `sa_attachment_required && !sa_attachment_provided`, but do not infer from that
  // that the requirement survives: Services Australia's handling of attachmentRequired once
  // evidence arrives is not pinned down by the spec, and observed behaviour is that it can go back
  // to false. Anything keyed on the requirement ALONE therefore disappears the moment a file lands
  // - which is why ClaimSahPushIcon enters its attachment branch on `required || provided`.
  sa_attachment_provided?: boolean | null;
  sa_further_information_required?: boolean | null;
  sa_push_error?: string | null;
  // TAV-2592. Two columns, but only ONE raises a flag - see requiresLateSubmissionReason in
  // lib/lateSubmission.ts. `sa_late_submission_required` mirrors Services Australia's read-only
  // `Item.requireLateSubmissionReason` and is the only source of a late-submission flag anywhere.
  // `is_late_submission` is TurnPoint's service-date-based hint, still populated but INFORMATIONAL
  // ONLY as of 2026-08-14 - nothing reads it. `late_submission_reason_code` /
  // `late_submission_justification` are user-captured, writable via the dedicated
  // `updateClaimItemLateSubmission` mutation (never through `updateClaimItem` - see EDITABLE_KEYS).
  is_late_submission?: boolean | null;
  sa_late_submission_required?: boolean | null;
  late_submission_reason_code?: string | null;
  late_submission_justification?: string | null;
  reconciliation_item?: ReconItem;
  statement?: Statement;
  alert_items?: ClaimItemAlert[];
}

// Per-item detail returned in a finalise response's `api.items[]` (TAV-2590). This array only
// ever contains items that need attention or failed to push — never every item on the claim.
export interface ClaimApiPushItem {
  claim_item_id: string;
  reference: string;
  sa_invoice_item_id: string | null;
  attachment_required: boolean;
  further_information_required: boolean;
  // TAV-2592. Mirrors `sa_late_submission_required` for this item at push time.
  late_submission_required: boolean;
  // The raw Services Australia `Item` for this row. Confirmed against the Invoices 1.2.0 spec:
  // `Item` has NO field naming which information is missing — only the boolean flag above. The
  // explanatory text is invoice-level, in `ClaimApiPushResult.messages`. This still carries the
  // other read-only fields SA computed for the row (itemStatus, attachmentProvided,
  // requireLateSubmissionReason, the *Text lookups), which is useful context beside a flagged row.
  further_information: Record<string, unknown> | null;
  error: string | null;
}

// One invoice-level diagnostic from Services Australia (`Message` in Invoices 1.2.0 — all fields
// read-only). This is where AC #7's "specific missing field" detail lives: `Item` only carries the
// boolean `furtherInformationRequired`, so the API makes one extra `GET /invoice/v1/{invoiceId}`
// after a push to collect these. Messages are NOT attributable to an individual claim item —
// `Message` has no item reference — so they are shown as a claim-level list.
export interface ClaimApiPushMessage {
  type?: string;
  shortText?: string;
  longText?: string;
}

export interface ClaimApiPushWarning {
  claim_item_id?: string;
  reference?: string;
  reason: string;
  // Which KIND of warning this is, so the UI can put each one where it belongs. Set by
  // syncClaimToApi; absent on the older finalise-path warnings, which are treated as unclassified.
  //
  //   'item_rejected'        Services Australia refused this row. ALREADY shown against the row
  //                          itself, parsed, on the push icon's popover - so the claim-level banner
  //                          must NOT repeat it. Doing so buried the banner's counts under a wall of
  //                          raw vendor JSON on any claim with a mix of accepted and rejected items.
  //   'item_incomplete'      the row is missing a field needed to build the payload at all.
  //   'attachment_reupload'  the item was recreated at Services Australia (its care recipient,
  //                          service or delivery date changed, none of which the vendor permits
  //                          updating), silently detaching any file already uploaded for it. The ONLY
  //                          kind the banner renders: nothing else on the page can tell the user this.
  //   'invoice'              claim-level, not attributable to a row.
  type?: 'item_rejected' | 'item_incomplete' | 'attachment_reupload' | 'invoice';
}

// The `api` field of a finalise response (TAV-2590). Has four shapes depending on the provider's
// PRODA connection state - see the comment on <ClaimApiPushSummary> in Claim.tsx:
//   - `null` (not this interface) for a non-PRODA provider: nothing was attempted.
//   - `{ attempted: true, sa_invoice_id, ... }` for the normal connected path.
//   - `{ attempted: true, failed: true, reason }` when the push threw before it could start
//     (expired auth / environment mismatch) - note NO counts are present on this shape.
//   - `{ attempted: false, skipped: true, reason }` when connected but the connection has no
//     `serviceProviderId` (the ACME/company-1 case encountered in local testing).
// All fields below except `attempted`/`items`/`warnings` are therefore optional - read them
// defensively, never assume a count field exists.
export interface ClaimApiPushResult {
  attempted: boolean;
  resuming?: boolean;
  failed?: boolean;
  skipped?: boolean;
  reason?: string;
  sa_invoice_id?: string;
  sa_invoice_status?: string | null;
  items_total?: number;
  items_pushed?: number;
  items_pending?: number;
  attachment_required_count?: number;
  further_information_required_count?: number;
  // TAV-2592.
  late_submission_required_count?: number;
  // The reconcile breakdown, present only on a /send-to-sa or /finalise-submit response - the
  // finalise path (pushClaimToApi) is add-only and reports none of these. `items_unchanged` is
  // deliberately included so a re-send that legitimately did nothing reads as "nothing to do"
  // rather than as a button that silently failed.
  //
  // `items_replaced` counts items Services Australia would not let us update in place: changing a
  // claim item's care recipient, SaH service or delivery date forces a delete + recreate, which
  // mints a new SA item id and detaches any attachment already uploaded for it. Those rows also
  // arrive in `warnings`.
  items_created?: number;
  items_updated?: number;
  items_deleted?: number;
  items_replaced?: number;
  items_unchanged?: number;
  // Invoice-level diagnostics from Services Australia. Only populated when at least one item needed
  // attention (the API skips the extra GET on a clean push), so an empty array is the normal case.
  messages?: ClaimApiPushMessage[];
  items: ClaimApiPushItem[];
  warnings: ClaimApiPushWarning[];
}

// The `data` payload of `POST .../finalise` (TAV-2590), i.e. what `finaliseClaim` resolves to
// after `unwrap()` strips the outer `{ success, message }` envelope (see actions/claims.ts).
export interface ClaimFinaliseResponse {
  finalised: boolean;
  api: ClaimApiPushResult | null;
}

// Services Australia `InvoiceSummary` (Invoices 1.2.0) — the ENTIRE body of a /submit or /open
// response. Note what is NOT here: no items[] and no messages[]. Per-item state after a submit comes
// from the refreshed claim-items query, and messages[] from the persisted claim.sa_meta_information.
export interface SaInvoiceSummary {
  invoiceId?: string;
  serviceNapsId?: string;
  serviceProviderId?: string;
  externalReferenceId?: string;
  status?: string | null;
  createdAtDateTime?: string;
  updatedAtDateTime?: string;
  asyncInProgress?: boolean;
}

// Post-`unwrap` shapes (TAV-2593) — the API wraps both in `data`, so unwrap strips one level, exactly
// like ClaimFinaliseResponse.
export interface ClaimSubmitResponse {
  submitted: boolean;
  sa_invoice_status: string | null;
  invoice: SaInvoiceSummary | null;
}

// `POST .../finalise-submit` after unwrap. Carries BOTH outcomes because the endpoint does both:
// `api` is the reconcile that ran immediately before lodging (so the UI can report what changed at
// Services Australia in the same breath as the submission), and the invoice fields are the
// submission itself.
//
// Note there is no partial-success shape. The claim moves draft -> submitted in one transaction, so
// a rejected submission is an error response and leaves the claim exactly as it was.
export interface ClaimFinaliseSubmitResponse {
  finalised: boolean;
  submitted: boolean;
  sa_invoice_status: string | null;
  invoice?: SaInvoiceSummary | null;
  api: ClaimApiPushResult;
}

// Services Australia `ClaimSummary` (Claim 1.0.0) - the ENTIRE body of the 201. Note what is NOT
// here: no invoices[] and no messages[]. Both live only on the full Claim from GET /claim/v1/{id},
// which is TAV-2595's surface; this ticket reads it server-side for membership only.
export interface SaClaimSummary {
  claimId?: string;
  serviceProviderId?: string;
  serviceNapsId?: string;
  claimType?: string;
  paymentDate?: string | null;
  status?: string | null;
  createdAtDateTime?: string;
  updatedAtDateTime?: string;
}

export interface ClaimableClaim {
  id: string;
  reference: string;
  sa_invoice_id: string | null;
  start_date: string;
  end_date: string;
  items_count: number;
}

// sa_connected means PRODA direct claiming is available for this company in this environment, as of
// TAV-3192: the `feature-proda-direct` flag (resolved company-wide, no user identity in the
// evaluation context) AND ProdaConnections#isConnected - see the controller comment on
// get_claimable.
export interface ClaimableResponse {
  sa_connected: boolean;
  claims: ClaimableClaim[];
  // TAV-2595. ISO timestamp of the last claim-status sweep for this company, or null if it has
  // never run. Read by the "Last PRODA Update" indicator so it is correct on FIRST paint, before
  // refreshClaimStatuses has resolved. Optional (rather than `| null`) so existing fixtures/mocks
  // predating this field keep typechecking - `Claims.tsx` already reads it defensively with `??`.
  claims_last_synced_at?: string | null;
}

// One error entry from a claim-status sweep (`POST .../claims/refresh-status`, TAV-2595) - a single
// claim's detail call or write failed and was isolated rather than aborting the whole sweep.
// `sa_claim_id` is OPTIONAL: the statement search runs once for the whole sweep, so its failure is
// not attributable to any one claim and carries a message alone.
export interface ClaimStatusSweepFailure {
  sa_claim_id?: string;
  message: string;
}

// TAV-2596. The other half of the same `errors[]` array, and a DIFFERENT shape: a payment item on a
// pulled statement that did not become a reconciliation item. It is not a failure of the sweep - the
// sweep worked, and this is its finding. `reason` is what separates an item needing provider
// attention (`UNMATCHED`) from one that was simply reconciled already (`SKIPPED`), and there is no
// `message` field, which is why reading these as ClaimStatusSweepFailure rendered blank.
//
// The API also stores this list durably on the claim (`sa_reconciliation_errors`), because the mixed
// case emits it exactly once: some items matched, so a reconciliation exists and the claim leaves the
// pull's candidate set for good.
export interface ClaimStatusUnreconciledItem {
  sa_claim_id: string;
  id: string | null;
  ref: string | null;
  reason: 'UNMATCHED' | 'SKIPPED';
  details: string;
}

export type ClaimStatusRefreshError = ClaimStatusSweepFailure | ClaimStatusUnreconciledItem;

// One claim that transitioned INTO `Approved` during this sweep. Retained as an informational
// transition report only - TAV-2596's reconciliation pull is LEVEL-triggered on current status plus
// the absence of a reconciliation, so it does not consume this. The web does nothing with it.
export interface ClaimStatusRefreshApproved {
  id: string;
}

// The sweep was skipped - nothing went wrong, the data is simply not due yet (or there is nothing
// to sweep). Still a 200. `reason` explains why; `last_synced_at` reflects the last time a sweep
// actually ran (null if it never has).
export interface ClaimStatusRefreshSkipped {
  checked: false;
  reason: 'no_claims' | 'fresh' | 'not_connected';
  last_synced_at: string | null;
  next_check_after: string | null;
  staleness_hours: number;
}

// The sweep ran. `claims_approved` and `errors` default to empty arrays server-side but are typed
// as always-present here since the API always returns them on this branch.
export interface ClaimStatusRefreshRan {
  checked: true;
  last_synced_at: string;
  next_check_after: string;
  staleness_hours: number;
  sa_claims_seen: number;
  sa_claims_updated: number;
  claims_updated: number;
  invoices_updated: number;
  claims_approved: ClaimStatusRefreshApproved[];
  // TAV-2596. The payment-statement reconciliation pull that runs after the sweep's transaction
  // commits. `reconciliations_created` is the only field the web branches on - it is what decides
  // whether the reconciliation list is now stale (see actions/claims.ts). The rest are reported for
  // parity with the API and for future surfacing.
  //
  // All five are OPTIONAL, following the `claims_last_synced_at` precedent above: the API half
  // ships on its own branch and its own pipeline, so a web bundle can be live against an API that
  // predates these fields. Typing them as required would assert a guarantee the deploy topology
  // does not give us, and would hide the absence from any future consumer that sums them (an older
  // API would yield NaN silently instead of a compile-time nudge to guard).
  reconciliations_created?: number;
  reconciliations_skipped?: number;
  statements_unavailable?: number;
  claim_dates_backfilled?: number;
  reconciliations_truncated?: boolean;
  truncated: boolean;
  errors: ClaimStatusRefreshError[];
}

// The `data` payload of `POST .../claims/refresh-status` (TAV-2595), after `unwrap()`. A
// discriminated union on `checked` - narrow on it rather than reading fields optimistically, since
// the two branches carry different fields.
export type ClaimStatusRefreshResult = ClaimStatusRefreshSkipped | ClaimStatusRefreshRan;

export interface SubmitClaimsResponse {
  claim: SaClaimSummary | null;
  sa_claim_id: string | null;
  sa_claim_status: string | null;
  reconciled_from: 'detail' | 'response';
  claims_submitted: ClaimableClaim[];
  claims_not_included: ClaimableClaim[];
}

export interface ClaimItemAlert {
  id: string;
  claim_item_id: string;
  alert_id: string;
  alert: {
    id: string;
    reference_id: string;
    message: string;
    alert_level: 'error' | 'warning';
  };
}

export interface ClaimItemFile {
  id: string;
  claim_item: ClaimItem;
  file_name: string;
  file_type: string;
  file_path: string;
}

// One Services Australia attachment on a claim item (TAV-2591). Mirrors InvoiceFile rather than the
// dead ClaimItemFile scaffolding - `data` holds the blob object URL produced by downloadFile().
export interface ClaimItemAttachment {
  id: string;
  claim_item_id: string;
  file_name: string;
  file_type: string;
  file_path: string;
  attachment_type: string | null;
  // null means "uploaded to Services Australia, but the follow-up read that resolves the vendor's
  // id failed". The file IS at SA; TurnPoint just cannot reference it. Rendered as a warning.
  sa_attachment_id: string | null;
  is_deleted?: boolean;
  created_at?: string;
  data?: string | null;
}

export interface UploadClaimItemAttachmentResponse {
  attachment: ClaimItemAttachment;
  warnings: string[];
}

export interface DeleteClaimItemAttachmentResponse {
  deleted: boolean;
  warnings: string[];
}

// Reconciliations
export interface Reconciliation {
  id: string;
  company_id: number;
  sah_reference_id: string;
  payment_date: string;
  sah_document: any;
  summary: ReconSummary;
  breakdown: ReconClient[];
  items: ReconItem[];
}

export interface ReconItem {
  id: string;
  reconciliation_id: string;
  funding_source_id: number;
  sah_invoice_id: string;
  sah_invoice_item_id: string;
  contribution_amount: number;
  final_payment_amount: number;
  meta_information?: any;
  claim_item?: ClaimItem;
}

export interface ReconSummary {
  total_paid?: number;
  total_gov?: number;
  total_co_contrib?: number;
  total_records?: number;
}

export interface ReconAmendmentItem {
  id: string;
  service: string;
  date: string;
  originalCost: number;
  newCost: number;
  govPaysChange: number;
  youPayChange: number;
  meta_budgets?: any[];
  funding_sources?: any[];
}

export interface ReconClient {
  client_id: number;
  full_name: string;
  items: ReconClientItem[];
  currentItems?: ReconClientItem[];
  amendmentGroups?: ReconAmendmentItem[];
  adjustmentGroups?: ReconAmendmentItem[];
  total_claimed: number;
  total_gov_contribution_amount: number;
  total_co_contribution_amount: number;
  total_paid: number;
  status: 'approved' | 'adjusted' | 'rejected';
  services_count: number;
}

export interface ReconClientItem {
  id: string;
  funding_source_ids?: number[];
  funding_sources?: any[];
  service_name: string;
  service_date: string;
  claimed: number;
  paid: number;
  gov_contribution_amount?: number;
  co_contribution_amount?: number;
  meta_budgets?: any[];
  reference?: string;
  sah_entitlement_type?: string;
  sah_invoice_item_id?: string;
}

// Statements
export interface StatementClient {
  id: number;
  first_name: string;
  surname: string;
  email?: string | null;
  statement_delivery_id?: string;
  statement_delivery?: string;
}

export interface Statement {
  id: string;
  status: StatementStatus;
  client_id: number;
  company_id: number;
  start_date: string;
  end_date: string;
  email_failure_reason?: string | null;
  client?: StatementClient;
}

// Client Invoices / Co-Contributions
export interface ClientInvoiceStatus {
  id: string;
  name: string;
  tag: string;
  is_active: boolean;
  colour: string;
  sequence: number;
}

export interface ClientInvoiceItem {
  id: string;
  invoice_id: string;
  service_date: string;
  description: string;
  units: number;
  unit_amount: number;
  created_at: string;
  createdby_id: number;
  updated_at: string;
  updatedby_id: number | null;
}

export interface ClientInvoice {
  id: string;
  status_id: string;
  client_id: number;
  company_id: number;
  invoice_date: string;
  reference: string | null;
  due_date: string;
  payment_source: ClientInvoicePaymentSource;
  created_at: string;
  createdby_id: number;
  updated_at: string;
  updatedby_id: number | null;
  status: ClientInvoiceStatus;
  items: ClientInvoiceItem[];
  client: ClientInvoiceClient;
  total_amount: number;
}

// `'gocardless'` is set by the GoCardless invoice-sync handler when a
// payment is reconciled — these rows are locked from manual updates.
// `'manual'` is set by the TAV-2053 status endpoints whenever a user
// transitions the status by hand. `null` means the invoice has never had
// its status touched outside the create flow (still eligible for manual
// updates).
export type ClientInvoicePaymentSource = 'gocardless' | 'manual' | null;

export type BulkInvoiceStatusSkipReason = 'gocardless_locked' | 'no_change' | 'invalid_transition' | 'unlock_required';

export interface BulkInvoiceStatusUpdateResponse {
  updated: string[];
  skipped: { id: string; reason: BulkInvoiceStatusSkipReason; current_status: string | null }[];
}

interface ClientInvoiceClient extends ClientRow {
  send_to: string;
}

// Sah Service Type Associations. Lives here rather than in `~lookups/models` because every consumer
// is inside this feature, and typing `registration` from `sahProviderSummary` would otherwise point
// the `lookups` feature back at `support-at-home`.
export interface SahServiceTypeAssociation extends Lookup {
  category: string;
  category_id?: string;
  service_type_ids: number[];
  service_info?: SahServiceInfo | null;
  // The period this occurrence of the service covers. A service whose contribution category
  // changes on a date exists as two occurrences sharing a name, so the dates are what tells them
  // apart on screen.
  start_date?: string | null;
  end_date?: string | null;
  // Service types that cannot be added to THIS occurrence because they are already associated with
  // a different service whose period overlaps it. A service type associated with a service over a
  // different, non-overlapping period is absent from this list and stays selectable.
  unavailable_service_type_ids?: number[];
  // TAV-3234 (AC5). The Services Australia registration record matching this SaH service's vendor
  // service type, or null when there is none. Deliberately the same shape as the Registered Services
  // tab's row, so the same badge renderer can consume it and the two screens cannot drift on what
  // "registered" means. Null is "no registration", which renders a BLANK cell, not a badge.
  registration?: SahRegisteredServiceType | null;
}
